import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Clock, ShieldCheck } from 'lucide-react';
import { requestPasswordReset, resetPassword } from '../../api/auth';
import { isValidNewPassword } from '../../utils/password';
import { formatPhone } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import AuthLayout from '../Login/AuthLayout';
import AuthField from '../Login/AuthField';
import AuthAlert from '../Login/AuthAlert';
import AuthSubmit from '../Login/AuthSubmit';
import {
  PHONE_RULE, normalizePhone, authErrorMessage, canAutoFocus, focusField, splitAtToken,
} from '../Login/authHelpers';

const SUPPORT_EMAIL = 'support@darsly.uz';
// The API allows five code requests a minute; asking again sooner only earns an error.
const RESEND_SECONDS = 60;

function ForgotPassword() {
  const { t } = useI18n();
  const location = useLocation();
  // "Back to sign in" returns to the sign-in page the visitor came from (the teacher portal
  // sends its own path); only the two known pages are honoured.
  const signInPath = location.state?.backTo === '/teacher-login' ? '/teacher-login' : '/login';
  const [step, setStep] = useState('phone'); // 'phone' -> 'code' -> 'done'
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState(''); // normalized, exactly as the API got it
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // { phone, code, password, confirm }
  const [error, setError] = useState(''); // what went wrong with the request as a whole
  const [cooldown, setCooldown] = useState(0); // seconds until another code may be asked for

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const clearField = (name) => {
    setError('');
    setFieldErrors((current) => ({ ...current, [name]: undefined }));
  };

  // Ask the API to send a code. The client rejects with { message, code } where `code` is the
  // HTTP status: 400 means too many codes were asked for, 429 is the rate limit (the client's
  // own wording); anything else stays generic so no English backend text reaches the page.
  const requestCode = async (target) => {
    setError('');
    setSubmitting(true);
    try {
      await requestPasswordReset(target);
      setPhone(target);
      setCode('');
      setCooldown(RESEND_SECONDS);
      setStep('code');
    } catch (err) {
      setError(err?.code === 400
        ? t('auth.forgotPassword.errorTooMany')
        : authErrorMessage(err, { fallback: t('auth.forgotPassword.errorDefault') }));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    const target = normalizePhone(phoneInput);
    if (!PHONE_RULE.test(target)) {
      setError('');
      setFieldErrors({ phone: t('auth.forgotPassword.errorPhone') });
      focusField('reset-phone');
      return undefined;
    }
    setFieldErrors({});
    return requestCode(target);
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = {};
    if (code.trim().length < 4) missing.code = t('auth.forgotPassword.errorCodeRequired');
    if (!isValidNewPassword(password)) missing.password = t('auth.forgotPassword.errorPasswordRules');
    else if (password !== confirm) missing.confirm = t('auth.forgotPassword.errorMismatch');
    setFieldErrors(missing);
    const first = ['code', 'password', 'confirm'].find((name) => missing[name]);
    if (first) {
      focusField({ code: 'reset-code', password: 'reset-password', confirm: 'reset-confirm' }[first]);
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({ phone, code: code.trim(), newPassword: password });
      setStep('done');
    } catch (err) {
      if (err?.code === 401) {
        setFieldErrors({ code: t('auth.forgotPassword.errorCode') });
        focusField('reset-code');
      } else {
        setError(authErrorMessage(err, { fallback: t('auth.forgotPassword.errorDefault') }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backToPhone = () => {
    setStep('phone');
    setError('');
    setFieldErrors({});
    setPassword('');
    setConfirm('');
  };

  const features = [
    { icon: <Lock size={18} />, title: t('auth.forgotPassword.feature1Title'), desc: t('auth.forgotPassword.feature1Desc') },
    { icon: <Clock size={18} />, title: t('auth.forgotPassword.feature2Title'), desc: t('auth.forgotPassword.feature2Desc') },
  ];

  const layout = {
    tagline: t('auth.forgotPassword.tagline1'),
    taglineEm: t('auth.forgotPassword.taglineEm'),
    desc: t('auth.forgotPassword.desc'),
    features,
  };

  if (step === 'done') {
    // The reset is over, so the brand panel stops asking "forgot your password?" and has nothing
    // left to explain about codes.
    return (
      <AuthLayout
        tagline={t('auth.forgotPassword.doneTagline1')}
        taglineEm={t('auth.forgotPassword.doneTaglineEm')}
        desc={t('auth.forgotPassword.doneDesc')}
        title={t('auth.forgotPassword.doneTitle')}
      >
        <div className="auth-state">
          <div className="auth-state-art" data-tone="success"><ShieldCheck aria-hidden="true" /></div>
          <div className="auth-form-header">
            <h1>{t('auth.forgotPassword.doneTitle')}</h1>
            <p>{t('auth.forgotPassword.doneSubtitle')}</p>
          </div>
          <Link to="/login" className="auth-submit">
            {t('auth.forgotPassword.goToSignIn')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (step === 'code') {
    return (
      <AuthLayout
        {...layout}
        title={t('auth.forgotPassword.codeTitle')}
        back={{ to: signInPath, label: t('auth.forgotPassword.backToSignIn') }}
        below={[
          <button
            type="button"
            className="auth-link"
            disabled={submitting || cooldown > 0}
            onClick={() => requestCode(phone)}
          >
            {cooldown > 0
              ? t('auth.forgotPassword.resendIn', { seconds: cooldown })
              : t('auth.forgotPassword.resendCode')}
          </button>,
          <button type="button" className="auth-link" onClick={backToPhone}>
            {t('auth.forgotPassword.changeNumber')}
          </button>,
        ]}
      >
        <div className="auth-form-header">
          <h1>{t('auth.forgotPassword.codeTitle')}</h1>
          <p>{t('auth.forgotPassword.codeSubtitle', { phone: formatPhone(phone) })}</p>
        </div>

        <form onSubmit={handleResetSubmit} noValidate aria-busy={submitting}>
          <AuthField
            id="reset-code"
            label={t('auth.forgotPassword.codeLabel')}
            value={code}
            onChange={(e) => { setCode(e.target.value); clearField('code'); }}
            placeholder={t('auth.forgotPassword.codePlaceholder')}
            error={fieldErrors.code}
            inputMode="numeric"
            maxLength={8}
            autoFocus={canAutoFocus()}
            autoComplete="one-time-code"
            readOnly={submitting}
          />

          <AuthField
            id="reset-password"
            type="password"
            label={t('auth.forgotPassword.newPasswordLabel')}
            value={password}
            onChange={(e) => { setPassword(e.target.value); clearField('password'); }}
            placeholder={t('auth.forgotPassword.passwordPlaceholder')}
            hint={t('auth.forgotPassword.passwordHint')}
            error={fieldErrors.password}
            autoComplete="new-password"
            readOnly={submitting}
          />

          <AuthField
            id="reset-confirm"
            type="password"
            label={t('auth.forgotPassword.confirmPasswordLabel')}
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); clearField('confirm'); }}
            placeholder={t('auth.forgotPassword.confirmPasswordPlaceholder')}
            error={fieldErrors.confirm}
            autoComplete="new-password"
            readOnly={submitting}
          />

          <AuthAlert>{error}</AuthAlert>

          <AuthSubmit
            busy={submitting}
            label={t('auth.forgotPassword.resetPassword')}
            busyLabel={t('auth.forgotPassword.resetting')}
          />
        </form>

        <p className="auth-note">{t('auth.forgotPassword.notReceived')}</p>
      </AuthLayout>
    );
  }

  const [noPhoneBefore, noPhoneAfter] = splitAtToken(t('auth.forgotPassword.noPhoneBody'), '{email}');

  return (
    <AuthLayout
      {...layout}
      title={t('auth.forgotPassword.title')}
      back={{ to: signInPath, label: t('auth.forgotPassword.backToSignIn') }}
      below={(
        <>
          {t('auth.forgotPassword.remembered')} <Link to={signInPath}>{t('auth.forgotPassword.signIn')}</Link>
        </>
      )}
    >
      <div className="auth-form-header">
        <h1>{t('auth.forgotPassword.title')}</h1>
        <p>{t('auth.forgotPassword.subtitle')}</p>
      </div>

      <form onSubmit={handlePhoneSubmit} noValidate aria-busy={submitting}>
        <AuthField
          id="reset-phone"
          type="tel"
          label={t('auth.forgotPassword.phoneLabel')}
          value={phoneInput}
          onChange={(e) => { setPhoneInput(e.target.value); clearField('phone'); }}
          placeholder={t('auth.forgotPassword.phonePlaceholder')}
          error={fieldErrors.phone}
          inputMode="tel"
          autoFocus={canAutoFocus()}
          autoComplete="tel"
          readOnly={submitting}
        />

        {/* An account without a phone number cannot get a code: say who can help, out of the way. */}
        <details className="auth-disclosure">
          <summary>{t('auth.forgotPassword.noPhoneTitle')}</summary>
          <p>
            {noPhoneBefore}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            {noPhoneAfter}
          </p>
        </details>

        <AuthAlert>{error}</AuthAlert>

        <AuthSubmit
          busy={submitting}
          label={t('auth.forgotPassword.sendCode')}
          busyLabel={t('auth.forgotPassword.sendingCode')}
        />
      </form>
    </AuthLayout>
  );
}

export default ForgotPassword;
